import { citygasRepository } from '@/repository/CitygasRepository';
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  GetStaticPaths,
  GetStaticPropsContext,
} from 'next';
import {
  addParamToLink,
  isString,
  queryToString,
  snakeCaseToCamelCase,
  validateCitygasCode,
} from '@/utils/string-utils';
import { AtCustByUserNumKeyRequestDTO } from '@/api/dto/request/AtCustRequestDTO';
import { customerRepository } from '@/repository/CustomerRepository';
import { captureException } from '@sentry/nextjs';
import { AutopayUserInfo } from '@/types/User';
import { CamelCaseCitygasInfo, CitygasAuthInfo, CitygasInfo } from '@/types/Citygas';
import { billRepository } from '@/repository/BillRepository';
import NoticeRequestDTO from '@/api/dto/request/NoticeRequestDTO';
import { BillDetail, DetailPropsType } from '@/types/Bill';
import { NonceRequestDTO } from '@/api/dto/request/NonceRequestDTO';
import { createEncryptedUrl } from '@/utils/browser-utils';
import { siteURL } from '@/constants/url';
// 반환 경로를 기반으로 페이지 생성 -
// 경로를 미리 지정. 빌드시 실행
export const getAllCitygasCodePaths: GetStaticPaths = async () => {
  const citygasCodes = await citygasRepository.getAllCitygasCode();

  return {
    paths: citygasCodes.map((citygasCode) => ({
      params: {
        citygasCode,
      },
    })),
    fallback: 'blocking',
  };
};

export async function getCitygasInfoProps({ params }: GetStaticPropsContext) {
  const citygasCode = validateCitygasCode(params?.citygasCode, params);
  if (!citygasCode) {
    return { notFound: true };
  }

  const citygasInfo = await citygasRepository.getCitygasInfo(citygasCode);

  if (!citygasInfo?.org_name) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      citygasInfo,
    },
  };
}

// 자동납부 신청/정보확인 외부접근 페이지 url 링크 serversideProps
export const getCitygasDecryptAutoUserInfoProps: GetServerSideProps = async (context) => {
  const cgcode = validateCitygasCode(context.params?.citygasCode, context);
  if (!cgcode) return { notFound: true };

  const userNumKey = queryToString(context.query.userNumKey);
  const external_yn = queryToString(context.query.external_yn) === 'N' ? 'N' : 'Y';
  if (!userNumKey) return { notFound: true };

  try {
    const dto = new AtCustByUserNumKeyRequestDTO(
      '',
      userNumKey,
      external_yn,
    );
    const res = await customerRepository.NP_AT_CUST(cgcode, dto);
    const customer = res?.data?.data?.results?.[0];

    if (!customer) return { notFound: true };

    const encryptParam = await citygasRepository.getEncryptParam({
      citygasCode: cgcode,
      info: userNumKey,
    });

    // recurrentId 존재 시 -> 정보 확인 페이지, 없으면 -> 신청 페이지
    const destination =
      customer.recurrentId && customer.recurrentId !== ''
        ? `/${cgcode}/autopay/out/info/${encryptParam}?external_yn=${external_yn}`
        : `/${cgcode}/autopay/out/apply/${encryptParam}?external_yn=${external_yn}`;

    return {
      redirect: {
        destination: addParamToLink(destination, {}),
        permanent: false,
      },
    };
  } catch {
    return { notFound: true };
  }
};

// 자동납부 신청/정보확인 페이지 url 링크 serversideProps
export const getCitygasAutoUserInfoProps: GetServerSideProps = async (context) => {
  const cgcode = validateCitygasCode(context.params?.citygasCode, context);
  if (!cgcode) {
    return { notFound: true };
  }

  const { encryptedQuery } = context.params!;
  const encryptInfo = queryToString(encryptedQuery);
  const external_yn = context.query.external_yn === 'N' ? 'N' : 'Y';

  try {
    const decryptedInfo = await citygasRepository.getDecryptParam({
      citygasCode: cgcode,
      encryptInfo,
    });
    const [userNumKey, userName] = decryptedInfo.split(':');

    if (!isString(userNumKey)) {
      return { notFound: true };
    }

    const requestAtCustDTO = new AtCustByUserNumKeyRequestDTO(
      external_yn === 'N' ? userName : '',
      userNumKey,
      external_yn
    );

    const [usIf, csIf] = await Promise.all([
      customerRepository.NP_AT_CUST(cgcode, requestAtCustDTO),
      citygasRepository.getCitygasAuthInfo(cgcode),
    ]);

    if (!csIf || !usIf?.data?.data?.results[0]) {
      return { notFound: true };
    }

    const userInfo = snakeCaseToCamelCase(usIf.data.data.results[0]) as AutopayUserInfo;
    if (!userInfo) {
      return { notFound: true };
    }

    return {
      props: {
        csIf,
        usIf: userInfo,
      },
    };
  } catch (e: any) {
    captureException(e, {
      tags: { error: JSON.stringify(e) },
    });
    if (e.code?.startsWith("E") && e.code.length === 4) {
      let path = "";
      let errorMessage = "";
      if (e.code === 'E800') {
        const info = e?.response?.data ?? {};
        const startInspectionDate = info.startInspectionDate;
        const endInspectionDate = info.endInspectionDate;

        if (cgcode && startInspectionDate && endInspectionDate) {
          path = `${siteURL}/${cgcode}/inspection?startInspectionInfo=${startInspectionDate}&endInspectionInfo=${endInspectionDate}&citygasCode=${cgcode}`;
        } else {
          path = `${siteURL}/citygas_error`;
          errorMessage = encodeURIComponent(e.message ?? '');
        }
      } else {
        path = `${siteURL}/citygas_error`;
        errorMessage = encodeURIComponent(e.message ?? '');
      }

      return {
        redirect: {
          destination: addParamToLink(path, {errorMessage}),
          permanent: false,
        },
      };
    }
    return { notFound: true };
  }
};

// decrypt 경로에서 암호화 파라미터를 복호화해 props를 구성하는 SSR
export const getCitygasAutoUserInfoPropsByDecrypt: GetServerSideProps = async (context) => {
  const cgcode = validateCitygasCode(context.params?.citygasCode, context);
  if (!cgcode) {
    return { notFound: true };
  }

  const { encryptedQuery } = context.params!;
  const encryptInfo = queryToString(encryptedQuery);
  const external_yn = context.query.external_yn === 'N' ? 'N' : 'Y';

  try {
    const decryptedInfo = await citygasRepository.getDecryptParam({
      citygasCode: cgcode,
      encryptInfo,
    });
    const [userNumKey, userName] = decryptedInfo.split(':');

    if (!isString(userNumKey)) {
      return { notFound: true };
    }

    const requestAtCustDTO = new AtCustByUserNumKeyRequestDTO(
      userName,
      userNumKey,
      external_yn
    );

    const [usIf, csIf] = await Promise.all([
      customerRepository.NP_AT_CUST(cgcode, requestAtCustDTO),
      citygasRepository.getCitygasAuthInfo(cgcode),
    ]);

    if (!csIf || !usIf?.data?.data?.results[0]) {
      return { notFound: true };
    }

    const userInfo = snakeCaseToCamelCase(usIf.data.data.results[0]) as AutopayUserInfo;
    if (!userInfo) {
      return { notFound: true };
    }

    return {
      props: {
        csIf,
        usIf: userInfo,
      },
    };
  } catch (e: any) {
    captureException(e, {
      tags: { error: JSON.stringify(e) },
    });
    if (e.code?.startsWith("E") && e.code.length === 4) {
      let path = "";
      let errorMessage = "";
      if (e.code === 'E800') {
        const info = e?.response?.data ?? {};
        const startInspectionDate = info.startInspectionDate;
        const endInspectionDate = info.endInspectionDate;

        if (cgcode && startInspectionDate && endInspectionDate) {
          path = `${siteURL}/${cgcode}/inspection?startInspectionInfo=${startInspectionDate}&endInspectionInfo=${endInspectionDate}&citygasCode=${cgcode}`;
        } else {
          path = `${siteURL}/citygas_error`;
          errorMessage = encodeURIComponent(e.message ?? '');
        }
      } else {
        path = `${siteURL}/citygas_error`;
        errorMessage = encodeURIComponent(e.message ?? '');
      }

      return {
        redirect: {
          destination: addParamToLink(path, {errorMessage}),
          permanent: false,
        },
      };
    }
    return { notFound: true };
  }
};
export const camelCaseCitygasInfo = (citygasInfo: CitygasInfo | CitygasAuthInfo) => {
  const {
    org_citygas_code: citygasCode,
    org_faq_number: citygasTelNo,
    org_name: citygasName,
    org_logo: citygasLogo,
  } = citygasInfo;

  let res: CamelCaseCitygasInfo = { citygasCode, citygasTelNo, citygasName, citygasLogo };

  if ('org_pay_chain_id' in citygasInfo) {
    const {
      org_autopay_chain_id: autoChainId,
      org_pay_chain_id: chainId,
      org_naverpay_client_id: clientId,
    } = citygasInfo;
    res = { autoChainId, chainId, clientId, ...res };
  }

  return res;
};

async function checkNonceToAccessPage(
  citygasCode: string,
  userNumKey: string,
  yearMonth: string,
  nonce: string,
): Promise<boolean> {
  try {
    const response = await citygasRepository.nonce(
      citygasCode,
      new NonceRequestDTO(userNumKey, yearMonth, nonce),
    );

    if (response === null) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
}

export const getDetailServerSideProps = async (
  context: GetServerSidePropsContext,
  options: { requireNonce: boolean; isEncrypted: boolean },
): Promise<GetServerSidePropsResult<DetailPropsType>> => {
  const { requireNonce, isEncrypted } = options;
  const res = await checkServerSideContext(context, isEncrypted, requireNonce);

  if ('notFound' in res) return { notFound: true };

  let { citygasCode, userNumKey, yearMonth, nonce } = res;
  nonce = queryToString(nonce);

  // if (requireNonce) {
  if (requireNonce && process.env.NEXT_PUBLIC_REAL_SERVER === 'true') {
    if (!nonce) return { notFound: true };
    const isAbleToAccess = await checkNonceToAccessPage(citygasCode, userNumKey, yearMonth, nonce);
    if (!isAbleToAccess) return { notFound: true };
  }

  return await commonDetailProps({ ...res, nonce });
};

// 고지납부 청구서
export const getServerSideBillDetailProps: GetServerSideProps<DetailPropsType> = async (
  context,
) => {
  return await getDetailServerSideProps(context, { requireNonce: true, isEncrypted: false });
};

export const getServerSideBillEncryptDetailProps: GetServerSideProps<DetailPropsType> = async (
  context,
) => {
  return await getDetailServerSideProps(context, { requireNonce: true, isEncrypted: true });
};

// 조회납부 청구서
export const getServerSideEncryptDetailProps: GetServerSideProps<DetailPropsType> = async (
  context,
) => {
  return await getDetailServerSideProps(context, { requireNonce: false, isEncrypted: true });
};

export const getServerSideDetailProps: GetServerSideProps<DetailPropsType> = async (context) => {
  const { citygasCode, userNumKey, yearMonth } = context.query as Record<string, string>;

  if (citygasCode && userNumKey && yearMonth) {
    return {
      redirect: {
        destination: await createEncryptedUrl(citygasCode, 'inquiry/detail', userNumKey, yearMonth),
        permanent: false,
      },
    };
  }
  
  return await getDetailServerSideProps(context, { requireNonce: false, isEncrypted: false });
};

export const checkServerSideContext = async (
  context: GetServerSidePropsContext,
  isEncrypted: boolean,
  requireNonce: boolean,
) => {
  let { citygasCode, userNumKey, yearMonth, nonce, resultCode, resultMessage } = context.query;

  // citygasCode 검증
  const cgcode = validateCitygasCode(citygasCode, context);
  if (!cgcode) {
    return { notFound: true };
  }

  if (isEncrypted) {
    const { encryptedQuery } = context.params ?? {};

    if (!encryptedQuery)
      return {
        notFound: true,
      };

    const encryptInfo = queryToString(encryptedQuery);
    const decryptedQuery = await citygasRepository.getDecryptParam({
      citygasCode: cgcode,
      encryptInfo,
    });
    [userNumKey, yearMonth] = decryptedQuery.split(':');
  }

  if (
    !isString(userNumKey) ||
    !isString(yearMonth) ||
    (resultCode && !isString(resultCode)) ||
    (resultMessage && !isString(resultMessage)) ||
    (requireNonce && !isString(nonce))
  ) {
    return {
      notFound: true,
    };
  }

  return { citygasCode: cgcode, userNumKey, yearMonth, nonce, resultCode, resultMessage };
};

export const commonDetailProps = async (res: {
  citygasCode: string;
  userNumKey: string;
  yearMonth: string;
  resultCode?: string;
  resultMessage?: string;
  nonce?: string;
}): Promise<{ props: DetailPropsType } | { redirect: { destination: string; permanent: boolean } } | { notFound: true }> => {
  const { citygasCode, userNumKey, yearMonth, resultCode, resultMessage, nonce } = res;

  try {
    const citygasInfo = await citygasRepository.getCitygasAuthInfo(citygasCode);

    const {
      org_name: citygasName,
      org_logo: citygasLogo,
      org_naverpay_client_id: citygasClientId,
      org_pay_chain_id: citygasNormalChainId,
      org_pay_card_chain_id: citygasCardChainId,
      org_pay_point_chain_id: citygasPointChainId,
      is_complex_pay: isComplexPay,
      is_prod: isProd
    } = citygasInfo;

    const res = await billRepository
      .NP_NOTICE(citygasCode, new NoticeRequestDTO(userNumKey, yearMonth))
      .then((res) => res.data.data);

    const billInfo: BillDetail = {
      title: res.title,
      userName: res.user_name,
      userNumKey: res.user_num_key,
      displayNumKey: res.display_num_key ?? res.user_num_key,
      displayNumTxt: res.display_num_txt ?? '고객번호',
      address: res.addr,
      amount: res.amount,
      taxScopeAmount: res.taxScopeAmount,
      taxExScopeAmount: res.taxExScopeAmount,
      yearMonth: res.year_month,
      expireDate: res.expire_date,
      payStatus: res.pay_st === '1',
      payButton: res.pay_button,
      payMethod: res.pay_mthd,
      differenceAmtText: res.amt_text ?? '',
      details: res.details ?? [],
    };

    let props: DetailPropsType = {
      citygasCode,
      citygasName,
      citygasLogo,
      citygasClientId,
      citygasNormalChainId,
      citygasCardChainId,
      citygasPointChainId,
      isComplexPay,
      isProd: isProd === 'Y' ? 'Y' : 'N',
      billInfo,
      ...(resultCode && { resultCode }),
      ...(resultMessage && { resultMessage }),
      ...(nonce && { nonce }),
    };

    return {
      props,
    };
  } catch (e: any) {
    if (e.code?.startsWith("E") && e.code.length === 4) {
      let path = "";
      let errorMessage = "";
      if (e.code === 'E800') {
        const info = e?.response?.data ?? {};
        const startInspectionDate = info.startInspectionDate;
        const endInspectionDate = info.endInspectionDate;
    
        if (citygasCode && startInspectionDate && endInspectionDate) {
          path = `${siteURL}/${citygasCode}/inspection?startInspectionInfo=${startInspectionDate}&endInspectionInfo=${endInspectionDate}&citygasCode=${citygasCode}`;
        } else {
          path = `${siteURL}/citygas_error`;
          errorMessage = encodeURIComponent(e.message ?? '');
        }
      } else {
        path = `${siteURL}/citygas_error`;
        errorMessage = encodeURIComponent(e.message ?? '');
      }

      return {
        redirect: {
          destination: addParamToLink(path, {errorMessage}),
          permanent: false,
        },
      };
    }

    return {
      notFound: true,
    };
  }
};

export const getErrorServerSideProps = async (ctx:GetServerSidePropsContext) => {
  const { errorMessage = '' } = ctx.query;

  return {
    props: {
      errorMessage,
    },
  };
};
